import { useEffect, useMemo, useState } from 'react';
import portalClient from './portalClient';
import PortalLayout from './PortalLayout';
import { Card, Skeleton, useToast } from '../design-system';
import './Portal.css';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : 'To be confirmed';

const STATE_TEXT = { filed: 'Filed', overdue: 'Overdue', upcoming: 'Upcoming' };

// Read-only filing status.
//
// Deliberately shows only period / due date / filed-or-not. Who is preparing
// it, how many hours have gone in, and where it sits in the firm's internal
// approval workflow are all excluded server-side — a client seeing "waiting for
// review by Priya" would raise questions the portal can't answer.
function PortalCompliance() {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalClient
      .get('/api/portal/compliance')
      .then((res) => setRows(res.data))
      .catch((err) => toast.danger(err.response?.data?.error || 'Could not load your filings'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Overdue first, then what's coming, then history — the order someone
  // actually reads this in.
  const { overdue, upcoming, filed } = useMemo(() => ({
    overdue: rows.filter((r) => r.state === 'overdue'),
    upcoming: rows.filter((r) => r.state === 'upcoming'),
    filed: rows.filter((r) => r.state === 'filed'),
  }), [rows]);

  const Section = ({ title, items, tone }) => {
    if (items.length === 0) return null;
    return (
      <Card padding="md">
        <div className="portal-card-head">
          <span className="portal-card-title">{title}</span>
          <span className="portal-muted">{items.length}</span>
        </div>
        <ul className="portal-list">
          {items.map((r) => (
            <li key={r.id} className="portal-list-row">
              <span className="portal-list-main">
                {r.name}
                <span className="portal-muted"> · {r.period}</span>
              </span>
              <span className={tone === 'danger' ? 'portal-overdue' : 'portal-muted'}>
                {r.state === 'filed'
                  ? `Filed ${fmtDate(r.filedDate)}`
                  : `Due ${fmtDate(r.dueDate)}`}
              </span>
            </li>
          ))}
        </ul>
      </Card>
    );
  };

  if (loading) {
    return <PortalLayout title="Filings"><Card><Skeleton.Text lines={5} /></Card></PortalLayout>;
  }

  return (
    <PortalLayout
      title="Filings"
      subtitle="Where your statutory filings stand. Your accountant keeps this up to date."
    >
      {rows.length === 0 ? (
        <Card padding="lg">
          <div className="portal-empty">
            <div className="portal-empty-icon">📅</div>
            <p className="portal-empty-title">No filings tracked yet</p>
            <p className="portal-muted">Once your compliance calendar is set up, it will show here.</p>
          </div>
        </Card>
      ) : (
        <div className="portal-sections">
          <Section title="Overdue" items={overdue} tone="danger" />
          <Section title="Coming up" items={upcoming} />
          <Section title="Filed" items={filed} />
        </div>
      )}

      <p className="portal-muted portal-footnote">
        {STATE_TEXT.filed} dates are recorded by your accountant. If something looks wrong,
        raise it on the Requests page.
      </p>
    </PortalLayout>
  );
}

export default PortalCompliance;
